import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, ChevronDown, FlaskConical, Link2, Loader2, Save, TestTubes, Trash2, Type } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { trpc } from '~/api/trpc';
import Content from '~/components/layout/Content';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Checkbox } from '~/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '~/components/ui/form';
import { Input } from '~/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { ScrollArea } from '~/components/ui/scroll-area';
import { Skeleton } from '~/components/ui/skeleton';
import { Switch } from '~/components/ui/switch';
import { Textarea } from '~/components/ui/textarea';
import { cn } from '~/lib/utils';

// Types
type TreatmentVariantOption = {
    id: string;
    name: string;
    treatmentName: string;
    assignedPanelId: string | null;
    assignedPanelName: string | null;
    stores: string;
};

// Zod schema matching the server
const tubeHandlingSchema = z.object({
    centrifuge: z.boolean(),
    centrifugeSettings: z.string().optional(),
    storage: z.string().min(1, 'Lagerung ist erforderlich'),
    notes: z.string(),
});

const panelFormSchema = z.object({
    remiPanelId: z.string().min(1, 'Remi Panel ID ist erforderlich'),
    anivaPanelId: z.string().optional(),
    name: z.string().min(1, 'Name ist erforderlich'),
    description: z.string(),
    edtaTubeCount: z.number().int().min(0),
    serumTubeCount: z.number().int().min(0),
    fluoridTubeCount: z.number().int().min(0),
    handlingInstructions: z.object({
        edta: tubeHandlingSchema.optional(),
        serum: tubeHandlingSchema.optional(),
        fluorid: tubeHandlingSchema.optional(),
    }),
    biomarkersText: z.string(),
    treatmentVariantIds: z.array(z.string()),
});

type PanelFormValues = z.infer<typeof panelFormSchema>;

const TUBE_DEFAULTS = {
    edta: { centrifuge: false, storage: 'Raumtemperatur', notes: '', centrifugeSettings: undefined },
    serum: { centrifuge: true, centrifugeSettings: '2000 g für 10-15 Minuten', storage: 'Gekühlt lagern (2-8°C)', notes: '' },
    fluorid: { centrifuge: true, centrifugeSettings: '2000 g für 10 Minuten', storage: 'Gekühlt lagern (2-8°C)', notes: '' },
} as const;

// Treatment Variant Multi-Select (reused from BloodTestManagement)
const TreatmentVariantMultiSelect = ({
    panelId,
    selectedIds,
    onSelectionChange,
    allTreatmentVariants,
}: {
    panelId: string;
    selectedIds: string[];
    onSelectionChange: (ids: string[]) => void;
    allTreatmentVariants: TreatmentVariantOption[];
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const selectedVariants = allTreatmentVariants.filter(tv => selectedIds.includes(tv.id));

    const handleToggle = (variantId: string) => {
        if (selectedIds.includes(variantId)) {
            onSelectionChange(selectedIds.filter(id => id !== variantId));
        } else {
            onSelectionChange([...selectedIds, variantId]);
        }
    };

    const groupedVariants = useMemo(() => {
        const query = search.toLowerCase();
        const filtered = query
            ? allTreatmentVariants.filter(tv =>
                tv.name.toLowerCase().includes(query) ||
                tv.treatmentName.toLowerCase().includes(query) ||
                tv.stores.toLowerCase().includes(query)
            )
            : allTreatmentVariants;

        const groups: Record<string, TreatmentVariantOption[]> = {};
        filtered.forEach(tv => {
            if (!groups[tv.treatmentName]) {
                groups[tv.treatmentName] = [];
            }
            groups[tv.treatmentName].push(tv);
        });
        return groups;
    }, [allTreatmentVariants, search]);

    return (
        <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setSearch(''); } }}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between h-auto min-h-10"
                    type="button"
                >
                    <div className="flex flex-wrap gap-1 py-1">
                        {selectedVariants.length === 0 ? (
                            <span className="text-muted-foreground">Behandlungsvarianten auswählen...</span>
                        ) : (
                            selectedVariants.map(tv => (
                                <Badge key={tv.id} variant="secondary" className="text-xs">
                                    {tv.name}
                                </Badge>
                            ))
                        )}
                    </div>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[400px] p-0" align="start">
                <div className="p-2 border-b">
                    <Input
                        placeholder="Suchen..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="h-8"
                    />
                </div>
                <ScrollArea className="h-[300px]">
                    <div className="p-2">
                        {Object.keys(groupedVariants).length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-4">Keine Ergebnisse</p>
                        ) : (
                            Object.entries(groupedVariants).map(([treatmentName, variants]) => (
                                <div key={treatmentName} className="mb-3">
                                    <div className="text-xs font-semibold text-muted-foreground px-2 py-1 bg-muted/50 rounded mb-1">
                                        {treatmentName}
                                    </div>
                                    {variants.map(tv => {
                                        const isSelected = selectedIds.includes(tv.id);
                                        const isAssignedToOtherPanel = tv.assignedPanelId !== null && tv.assignedPanelId !== panelId;

                                        return (
                                            <div
                                                key={tv.id}
                                                className={cn(
                                                    "flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-accent",
                                                    isAssignedToOtherPanel && "opacity-50 cursor-not-allowed hover:bg-transparent"
                                                )}
                                                onClick={() => !isAssignedToOtherPanel && handleToggle(tv.id)}
                                            >
                                                <Checkbox
                                                    checked={isSelected}
                                                    disabled={isAssignedToOtherPanel}
                                                    onCheckedChange={() => !isAssignedToOtherPanel && handleToggle(tv.id)}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-row gap-2 items-center">
                                                        <div className="text-sm truncate">{tv.name}</div>
                                                        <div className="text-xs truncate text-gray-400">{tv.stores}</div>
                                                    </div>
                                                    {isAssignedToOtherPanel && (
                                                        <div className="text-xs text-muted-foreground">
                                                            Bereits in: {tv.assignedPanelName}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))
                        )}
                    </div>
                </ScrollArea>
            </PopoverContent>
        </Popover>
    );
};

// Handling instructions form for a single tube type
const TubeHandlingForm = ({
    tubeKey,
    label,
    colorClass,
    form,
}: {
    tubeKey: 'edta' | 'serum' | 'fluorid';
    label: string;
    colorClass: string;
    form: ReturnType<typeof useForm<PanelFormValues>>;
}) => {
    const [isOpen, setIsOpen] = useState(true);
    const values = form.watch(`handlingInstructions.${tubeKey}`);

    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full justify-between p-2 h-auto" type="button">
                    <div className="flex items-center gap-2">
                        <span className={cn("w-3 h-3 rounded-full", colorClass)}></span>
                        <span className="text-sm font-medium">{label}</span>
                    </div>
                    <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
                </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 pl-5 pr-2 pb-3">
                <div className="flex items-center gap-4">
                    <FormField
                        control={form.control}
                        name={`handlingInstructions.${tubeKey}.centrifuge`}
                        render={({ field }) => (
                            <FormItem className="flex items-center gap-2 space-y-0">
                                <FormControl>
                                    <Switch checked={field.value ?? false} onCheckedChange={field.onChange} />
                                </FormControl>
                                <FormLabel className="mt-0!">Zentrifugieren</FormLabel>
                            </FormItem>
                        )}
                    />
                </div>

                {values?.centrifuge && (
                    <FormField
                        control={form.control}
                        name={`handlingInstructions.${tubeKey}.centrifugeSettings`}
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Zentrifugen-Einstellungen</FormLabel>
                                <FormControl>
                                    <Input {...field} value={field.value ?? ''} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                )}

                <FormField
                    control={form.control}
                    name={`handlingInstructions.${tubeKey}.storage`}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Lagerung</FormLabel>
                            <FormControl>
                                <Input {...field} value={field.value ?? ''} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name={`handlingInstructions.${tubeKey}.notes`}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Hinweise</FormLabel>
                            <FormControl>
                                <Textarea {...field} value={field.value ?? ''} rows={3} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </CollapsibleContent>
        </Collapsible>
    );
};

// Main page component
const BloodTestPanelDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isNew = id === 'new';
    const trpcUtils = trpc.useUtils();

    const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);

    const { data: panelData, isLoading: isLoadingPanel } = trpc.bloodTests.getPanel.useQuery(
        { panelId: id! },
        { enabled: !isNew && !!id }
    );

    const { data: allTreatmentVariants, isLoading: isLoadingVariants } = trpc.bloodTests.getAllTreatmentVariants.useQuery();

    const form = useForm<PanelFormValues>({
        resolver: zodResolver(panelFormSchema),
        defaultValues: {
            remiPanelId: '',
            anivaPanelId: '',
            name: '',
            description: '',
            edtaTubeCount: 0,
            serumTubeCount: 0,
            fluoridTubeCount: 0,
            handlingInstructions: {},
            biomarkersText: '',
            treatmentVariantIds: [],
        },
    });

    useEffect(() => {
        if (panelData) {
            const instructions = panelData.handlingInstructions as PanelFormValues['handlingInstructions'] | null;
            form.reset({
                remiPanelId: panelData.remiPanelId,
                anivaPanelId: panelData.anivaPanelId ?? '',
                name: panelData.name,
                description: panelData.description,
                edtaTubeCount: panelData.edtaTubeCount,
                serumTubeCount: panelData.serumTubeCount,
                fluoridTubeCount: panelData.fluoridTubeCount,
                handlingInstructions: instructions ?? {},
                biomarkersText: panelData.biomarkers.join('\n'),
                treatmentVariantIds: panelData.treatmentVariants.map(tv => tv.id),
            });
        }
    }, [panelData, form]);

    const edtaCount = form.watch('edtaTubeCount');
    const serumCount = form.watch('serumTubeCount');
    const fluoridCount = form.watch('fluoridTubeCount');

    useEffect(() => {
        if (edtaCount > 0 && !form.getValues('handlingInstructions.edta')) {
            form.setValue('handlingInstructions.edta', { ...TUBE_DEFAULTS.edta });
        } else if (edtaCount === 0) {
            form.setValue('handlingInstructions.edta', undefined);
        }
    }, [edtaCount, form]);

    useEffect(() => {
        if (serumCount > 0 && !form.getValues('handlingInstructions.serum')) {
            form.setValue('handlingInstructions.serum', { ...TUBE_DEFAULTS.serum });
        } else if (serumCount === 0) {
            form.setValue('handlingInstructions.serum', undefined);
        }
    }, [serumCount, form]);

    useEffect(() => {
        if (fluoridCount > 0 && !form.getValues('handlingInstructions.fluorid')) {
            form.setValue('handlingInstructions.fluorid', { ...TUBE_DEFAULTS.fluorid });
        } else if (fluoridCount === 0) {
            form.setValue('handlingInstructions.fluorid', undefined);
        }
    }, [fluoridCount, form]);

    const createMutation = trpc.bloodTests.createPanel.useMutation({
        onSuccess: (result) => {
            toast.success('Panel erstellt');
            trpcUtils.bloodTests.listPanels.invalidate();
            navigate(`/blood-tests/panel/${result.id}`, { replace: true });
        },
        onError: (error) => {
            toast.error('Fehler beim Erstellen', { description: error.message });
        },
    });

    const updateMutation = trpc.bloodTests.updatePanel.useMutation({
        onSuccess: () => {
            toast.success('Panel gespeichert');
            trpcUtils.bloodTests.listPanels.invalidate();
            trpcUtils.bloodTests.getPanel.invalidate({ panelId: id! });
            trpcUtils.bloodTests.getAllTreatmentVariants.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Speichern', { description: error.message });
        },
    });

    const archiveMutation = trpc.bloodTests.archivePanel.useMutation({
        onSuccess: () => {
            toast.success('Panel archiviert');
            trpcUtils.bloodTests.listPanels.invalidate();
            navigate('/blood-tests');
        },
        onError: (error) => {
            toast.error('Fehler beim Archivieren', { description: error.message });
        },
    });

    const onSubmit = (values: PanelFormValues) => {
        const biomarkers = values.biomarkersText
            .split('\n')
            .map(b => b.trim())
            .filter(b => b.length > 0);

        const payload = {
            remiPanelId: values.remiPanelId,
            anivaPanelId: values.anivaPanelId || undefined,
            name: values.name,
            description: values.description,
            edtaTubeCount: values.edtaTubeCount,
            serumTubeCount: values.serumTubeCount,
            fluoridTubeCount: values.fluoridTubeCount,
            handlingInstructions: values.handlingInstructions,
            biomarkers,
            treatmentVariantIds: values.treatmentVariantIds,
        };

        if (isNew) {
            createMutation.mutate(payload);
        } else {
            updateMutation.mutate({ panelId: id!, ...payload });
        }
    };

    const isSaving = createMutation.isPending || updateMutation.isPending;
    const biomarkersText = form.watch('biomarkersText');
    const biomarkerCount = biomarkersText ? biomarkersText.split('\n').filter(l => l.trim()).length : 0;

    if (!isNew && isLoadingPanel) {
        return (
            <Content>
                <div className="container mx-auto py-10 max-w-4xl">
                    <Skeleton className="h-8 w-48 mb-6" />
                    <div className="grid gap-6 md:grid-cols-2">
                        <Skeleton className="h-48" />
                        <Skeleton className="h-48" />
                        <Skeleton className="h-64 md:col-span-2" />
                    </div>
                </div>
            </Content>
        );
    }

    if (!isNew && !panelData) {
        return (
            <Content>
                <div className="container mx-auto py-10">
                    <div className="text-center py-12">
                        <h2 className="text-xl font-semibold mb-2">Panel nicht gefunden</h2>
                        <Button variant="outline" onClick={() => navigate('/blood-tests')}>
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Zurück zur Übersicht
                        </Button>
                    </div>
                </div>
            </Content>
        );
    }

    return (
        <Content>
            <div className="container mx-auto py-10 max-w-4xl">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/blood-tests')}>
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <div className="flex-1">
                            <div className="flex items-center gap-3">
                                <h1 className="text-2xl font-bold">
                                    {isNew ? 'Neues Panel' : panelData?.name ?? 'Panel'}
                                </h1>
                                {!isNew && (
                                    <Badge variant="outline" className="text-sm">Bluttest-Panel</Badge>
                                )}
                            </div>
                            {!isNew && panelData?.remiPanelId && (
                                <p className="text-sm text-muted-foreground font-mono">{panelData.remiPanelId}</p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            onClick={form.handleSubmit(onSubmit)}
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Speichern...
                                </>
                            ) : (
                                <>
                                    <Save className="mr-2 h-4 w-4" />
                                    {isNew ? 'Erstellen' : 'Speichern'}
                                </>
                            )}
                        </Button>
                    </div>
                </div>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <div className="grid gap-6 md:grid-cols-2">
                            {/* Basic Info */}
                            <Card className="md:col-span-2">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Type className="h-5 w-5" />
                                        Allgemein
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <FormField
                                            control={form.control}
                                            name="name"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Name</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder="z.B. Core Health - Basis Health Check" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="remiPanelId"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Remi Panel ID</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder="z.B. kalia-health-check" className="font-mono" {...field} />
                                                    </FormControl>
                                                    <FormDescription>Eindeutige ID für die Remi Health Integration</FormDescription>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>

                                    <FormField
                                        control={form.control}
                                        name="anivaPanelId"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Aniva Panel ID</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="z.B. 12345" className="font-mono" {...field} />
                                                </FormControl>
                                                <FormDescription>Panel ID für die Aniva Integration</FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="description"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Beschreibung</FormLabel>
                                                <FormControl>
                                                    <Textarea
                                                        placeholder="Beschreibung des Panels..."
                                                        rows={3}
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </CardContent>
                            </Card>

                            {/* Tube Requirements */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <TestTubes className="h-5 w-5" />
                                        Röhrchen
                                    </CardTitle>
                                    <CardDescription>Anzahl der benötigten Röhrchen pro Typ</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <FormField
                                        control={form.control}
                                        name="edtaTubeCount"
                                        render={({ field }) => (
                                            <FormItem>
                                                <div className="flex justify-between items-center">
                                                    <FormLabel className="flex items-center gap-2">
                                                        <span className="w-3 h-3 rounded-full bg-red-500"></span>
                                                        EDTA (rot)
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            type="number"
                                                            min={0}
                                                            className="w-20 text-center"
                                                            {...field}
                                                            onChange={e => field.onChange(parseInt(e.target.value) || 0)}
                                                        />
                                                    </FormControl>
                                                </div>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="serumTubeCount"
                                        render={({ field }) => (
                                            <FormItem>
                                                <div className="flex justify-between items-center">
                                                    <FormLabel className="flex items-center gap-2">
                                                        <span className="w-3 h-3 rounded-full bg-amber-800"></span>
                                                        Serum (braun)
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            type="number"
                                                            min={0}
                                                            className="w-20 text-center"
                                                            {...field}
                                                            onChange={e => field.onChange(parseInt(e.target.value) || 0)}
                                                        />
                                                    </FormControl>
                                                </div>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="fluoridTubeCount"
                                        render={({ field }) => (
                                            <FormItem>
                                                <div className="flex justify-between items-center">
                                                    <FormLabel className="flex items-center gap-2">
                                                        <span className="w-3 h-3 rounded-full bg-gray-500"></span>
                                                        GlucoEXACT (grau)
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            type="number"
                                                            min={0}
                                                            className="w-20 text-center"
                                                            {...field}
                                                            onChange={e => field.onChange(parseInt(e.target.value) || 0)}
                                                        />
                                                    </FormControl>
                                                </div>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </CardContent>
                            </Card>

                            {/* Treatment Variants */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Link2 className="h-5 w-5" />
                                        Verknüpfte Behandlungen
                                    </CardTitle>
                                    <CardDescription>
                                        Termine mit diesen Behandlungen erhalten automatisch dieses Panel
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {isLoadingVariants ? (
                                        <Skeleton className="h-10 w-full" />
                                    ) : (
                                        <FormField
                                            control={form.control}
                                            name="treatmentVariantIds"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormControl>
                                                        <TreatmentVariantMultiSelect
                                                            panelId={isNew ? '' : id!}
                                                            selectedIds={field.value}
                                                            onSelectionChange={field.onChange}
                                                            allTreatmentVariants={allTreatmentVariants ?? []}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    )}
                                </CardContent>
                            </Card>

                            {/* Handling Instructions */}
                            {(edtaCount > 0 || serumCount > 0 || fluoridCount > 0) && (
                                <Card className="md:col-span-2">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <FlaskConical className="h-5 w-5" />
                                            Handhabungsanweisungen
                                        </CardTitle>
                                        <CardDescription>Lagerungs- und Verarbeitungshinweise pro Röhrchentyp</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-2">
                                        {edtaCount > 0 && (
                                            <TubeHandlingForm
                                                tubeKey="edta"
                                                label="EDTA (rot)"
                                                colorClass="bg-red-500"
                                                form={form}
                                            />
                                        )}
                                        {serumCount > 0 && (
                                            <TubeHandlingForm
                                                tubeKey="serum"
                                                label="Serum (braun)"
                                                colorClass="bg-amber-800"
                                                form={form}
                                            />
                                        )}
                                        {fluoridCount > 0 && (
                                            <TubeHandlingForm
                                                tubeKey="fluorid"
                                                label="GlucoEXACT (grau)"
                                                colorClass="bg-gray-500"
                                                form={form}
                                            />
                                        )}
                                    </CardContent>
                                </Card>
                            )}

                            {/* Biomarkers */}
                            <Card className="md:col-span-2">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <FlaskConical className="h-5 w-5" />
                                        Biomarker
                                        <Badge variant="secondary" className="ml-1">{biomarkerCount}</Badge>
                                    </CardTitle>
                                    <CardDescription>Ein Biomarker pro Zeile</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <FormField
                                        control={form.control}
                                        name="biomarkersText"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Textarea
                                                        placeholder={"Albumin\nAlkalische Phosphatase\nAmylase\n..."}
                                                        rows={8}
                                                        className="font-mono text-sm"
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </CardContent>
                            </Card>
                        </div>
                    </form>
                </Form>

                {/* Danger Zone */}
                {!isNew && (
                    <Card className="mt-6 border-destructive/50">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-destructive">
                                <Trash2 className="h-5 w-5" />
                                Gefahrenzone
                            </CardTitle>
                            <CardDescription>
                                Diese Aktion kann nicht rückgängig gemacht werden
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium">Panel archivieren</p>
                                    <p className="text-sm text-muted-foreground">
                                        Das Panel wird nicht mehr in der Liste angezeigt. Bestehende Labor-Aufträge bleiben erhalten.
                                    </p>
                                </div>
                                <Button
                                    type="button"
                                    variant="destructive"
                                    onClick={() => setIsArchiveDialogOpen(true)}
                                >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Archivieren
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Archive Confirmation Dialog */}
                <Dialog open={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Panel archivieren</DialogTitle>
                            <DialogDescription>
                                Möchten Sie das Panel &quot;{panelData?.name}&quot; wirklich archivieren?
                                Es wird nicht mehr in der Liste angezeigt, bestehende Labor-Aufträge bleiben erhalten.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setIsArchiveDialogOpen(false)}>
                                Abbrechen
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={() => archiveMutation.mutate({ panelId: id! })}
                                disabled={archiveMutation.isPending}
                            >
                                {archiveMutation.isPending ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Archivieren...
                                    </>
                                ) : (
                                    'Archivieren'
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Content>
    );
};

export default BloodTestPanelDetail;
